import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatDividerModule} from '@angular/material/divider';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition} from '@angular/material/snack-bar';
import {debounceTime, Subscription, switchMap, tap} from 'rxjs';
import {UtilisateurService} from '../../../../services/utilisateurs/utilisateur.service';
import {Utilisateur} from '../../../../models/utilisateur/UtilisateurResponse';
import {Params} from '@angular/router';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {GroupeUsersService} from '../../../../services/groupe-users/groupe-users.service';
import {GroupesService} from '../../../../services/groupes/groupes.service';
import {GroupeUserBody} from '../../../../models/groupeUsers/GroupeUserBody';
import {GroupeBody} from '../../../../models/groupes/GroupeBody';
import {Groupe} from '../../../../models/groupes/Groupe';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'app-creer-modifier-groupe-diffusion-dialogue',
  standalone: true,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonToggleModule, MatButtonModule,
            MatDialogModule, MatDividerModule, MatIconModule, MatSnackBarModule, MatAutocompleteModule, CommonModule],
  templateUrl: './creer-modifier-groupe-diffusion-dialogue.component.html',
  styleUrl: './creer-modifier-groupe-diffusion-dialogue.component.scss'
})
export class CreerModifierGroupeDiffusionDialogueComponent implements OnInit, OnDestroy{
  formSubmitted: boolean = false;
  isEditMode = false;
  verticalPosition: MatSnackBarVerticalPosition = 'top';
  groupeDiffusionForm = new FormGroup({
    titre: new FormControl('', Validators.required),
    actif: new FormControl(true),
    referentNomPrenom: new FormControl(''),
    referentEmail: new FormControl('', [Validators.email]),
    referentTelephone: new FormControl('', [Validators.pattern('^[0-9]{10}$')]),
    utilisateur: new FormControl('')
  });
  users: Utilisateur[] = [];
  filteredUsers: Utilisateur[] = [];
  selectedUsers: Utilisateur[] = [];
  filterGeneral = '';
  subscriptions: Subscription[] = [];

  constructor(private dialogRef: MatDialogRef<CreerModifierGroupeDiffusionDialogueComponent>,
              @Inject(MAT_DIALOG_DATA) public data: { groupe?: Groupe },
              private utilisateurService: UtilisateurService,
              private groupeUsersService: GroupeUsersService,
              private groupesService: GroupesService,
              public snackBar: MatSnackBar) {
  }


  ngOnInit(): void {
    if(this.data) this.isEditMode = true;
    console.log('this.data', this.data);
    if(this.isEditMode){
      if(this.data?.groupe){
        this.groupeDiffusionForm.patchValue({
          titre: this.data.groupe.titre,
          actif: this.data.groupe.actif,
          referentNomPrenom: this.data.groupe.referent,
          referentEmail: this.data.groupe.email,
          referentTelephone: this.data.groupe.telephone
        });
      }
      if (this.data.groupe?.groupeUsers) {
        this.selectedUsers = this.data.groupe?.groupeUsers.map(userRelation => userRelation.user);
      }
    }

    this.loadUsers();

    this.subscriptions.push(
      this.groupeDiffusionForm.controls['utilisateur'].valueChanges.pipe(
        debounceTime(300),
        tap(value => {
          if (typeof value === 'string' && value.length >= 3) {
            this.filterGeneral = value;
          } else {
            this.filteredUsers = [];
          }
        }),
        switchMap(value => {
          if (typeof value === 'string' && value.length >= 3) {
            const params: Params = {
              limit: 500,
              generalFilter: this.filterGeneral,
              idApplication: 2
            };
            return this.utilisateurService.getUsers(params);
          } else {
            return [];
          }
        })
      ).subscribe({
        next: (response: any) => {
          this.filteredUsers = response?.data || [];
        },
        error: (err) => console.error('Erreur lors de la recherche des utilisateurs', err)
      })
    );
  }

  loadUsers() {
    const params: Params = {
      limit: 500,
      generalFilter: this.filterGeneral,
      idApplication: 2
    };
    this.subscriptions.push(
      this.utilisateurService.getUsers(params).subscribe({
        next: (users) => {
          this.users = users.data;
        },
        error: (error) => {
          console.error('Erreur lors de la récupération des utilisateurs', error);
        }
      })
    )
  }

  displayUser(user: Utilisateur): string {
    return user ? `${user.surname} ${user.givenName} - ${user.mail}` : '';
  }

  onUserInput() {
    const value = this.groupeDiffusionForm.get('utilisateur')?.value;
    if (!value || typeof value !== 'string') {
      this.filteredUsers = [];
    }
  }

  onUserSelected(event: any) {
    const selectedUser = event.option.value;
    console.log('this.selectedUsers', this.selectedUsers);
    if (!this.selectedUsers.includes(selectedUser)) {
      this.selectedUsers.push(selectedUser);
      this.groupeDiffusionForm.controls['utilisateur'].setValue('');
      this.filteredUsers = [];
      console.log('this.selectedUsers', this.selectedUsers);
    }
  }

  onAddUser(): void {
    const selectedUser = this.groupeDiffusionForm.controls['utilisateur'].value;

    if (selectedUser && typeof selectedUser === 'object' && !this.selectedUsers.includes(selectedUser)) {
      this.selectedUsers.push(selectedUser);
      this.groupeDiffusionForm.controls['utilisateur'].setValue('');
      this.filteredUsers = [];
      console.log('this.selectedUsers', this.selectedUsers);
    } else {
      console.log('Aucun utilisateur sélectionné ou déjà ajouté');
    }
  }

  onRemoveUser(user: Utilisateur): void {
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== user.id);
  }

  handleUsersUpdate() {
    const originalUsers = this.data.groupe?.groupeUsers.map(userRel => userRel.user) || [];
    const originalUserIds = originalUsers.map(user => user.id);
    const currentUserIds = this.selectedUsers.map(user => user.id);

    const addedUsers = this.selectedUsers.filter(user => !originalUserIds.includes(user.id));
    const removedUsers = this.data.groupe?.groupeUsers.filter(userRel => !currentUserIds.includes(userRel.user.id)) || [];

    addedUsers.forEach(user => {
      const groupeUserBody: GroupeUserBody = {
        users_id: user.id,
        groupe_id: this.data.groupe!.id
      };
      this.subscriptions.push(
        this.groupeUsersService.postGroupeUser(groupeUserBody).subscribe({
          next: () => {
            console.log(`Utilisateur ${user.surname} ajouté au groupe.`);
          },
          error: (err) => {
            console.error(`Erreur lors de l'ajout de l'utilisateur ${user.surname}:`, err);
          }
        })
      );
    });

    removedUsers.forEach(userRel => {
      this.subscriptions.push(
        this.groupeUsersService.deleteGroupeUser(userRel.id).subscribe({
          next: () => {
            console.log(`Utilisateur ${userRel.user.surname} supprimé du groupe.`);
          },
          error: (err) => {
            console.error(`Erreur lors de la suppression de l'utilisateur ${userRel.user.surname}:`, err);
          }
        })
      );
    });
  }

  onSubmit(): void {
    this.formSubmitted = true;

    if (this.groupeDiffusionForm.valid && this.selectedUsers.length > 0) {
      const groupeBody: GroupeBody = {
        titre: this.groupeDiffusionForm.controls['titre'].value ?? '',
        actif: this.groupeDiffusionForm.controls['actif'].value ?? true,
        referent: this.groupeDiffusionForm.controls['referentNomPrenom'].value || undefined,
        email: this.groupeDiffusionForm.controls['referentEmail'].value || undefined,
        telephone: this.groupeDiffusionForm.controls['referentTelephone'].value || undefined,
      };

      if (this.isEditMode && this.data.groupe?.id) {
        this.updateGroupe(groupeBody);
      } else {
        this.createGroupe(groupeBody);
      }
    } else {
      this.snackBar.open('Veuillez remplir tous les champs requis.', 'Fermer', {
        duration: 3000,
        verticalPosition: this.verticalPosition,
      });
    }
  }

  private createGroupe(groupeBody: GroupeBody) {
    this.subscriptions.push(
      this.groupesService.postGroupe(groupeBody).subscribe({
        next: (response) => {
          const groupeId = response.data.id;
          console.log('Groupe créé avec succès:', groupeId);

          this.addUsersToGroupe(groupeId);

          this.snackBar.open('Le groupe de diffusion est créé', 'Fermer', {
            duration: 3000,
            verticalPosition: this.verticalPosition,
          });

          this.dialogRef.close();
        },
        error: (err) => {
          console.error('Erreur lors de la création du groupe de diffusion:', err);
          this.snackBar.open('Erreur technique : le groupe n\'a pas été créé', 'Fermer', {
            duration: 3000,
            verticalPosition: this.verticalPosition,
          });
        }
      })
    );
  }

  private addUsersToGroupe(groupeId: number) {
    this.selectedUsers.forEach(user => {
      const groupeUserBody: GroupeUserBody = {
        users_id: user.id,
        groupe_id: groupeId
      };

      this.subscriptions.push(
        this.groupeUsersService.postGroupeUser(groupeUserBody).subscribe({
          next: () => {
            console.log(`Utilisateur ${user.surname} ajouté au groupe.`);
          },
          error: (err) => {
            console.error(`Erreur lors de l'ajout de l'utilisateur ${user.surname}:`, err);
          }
        })
      );
    });
  }

  private updateGroupe(groupeBody: GroupeBody) {
    if (!this.data.groupe?.id) return;

    this.subscriptions.push(
      this.groupesService.updateGroupe(this.data.groupe.id, groupeBody).subscribe({
        next: () => {
          console.log('Groupe mis à jour avec succès:', this.data.groupe?.id);

          this.handleUsersUpdate();

          this.snackBar.open('Le groupe de diffusion a été mis à jour', 'Fermer', {
            duration: 3000,
            verticalPosition: this.verticalPosition,
          });

          this.dialogRef.close();
        },
        error: (err) => {
          console.error('Erreur lors de la mise à jour du groupe:', err);
          this.snackBar.open('Erreur technique : le groupe n\'a pas été mis à jour', 'Fermer', {
            duration: 3000,
            verticalPosition: this.verticalPosition,
          });
        }
      })
    );
  }

  onClose() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }
}
